<template>
  <div class="space-y-8">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <p class="max-w-prose text-sm text-obsidian-500">{{ t('intro') }}</p>
      <CVButton v-if="canCreate" variant="default" @click="handleAdd">
        <PlusIcon class="size-4" /> {{ t('addEntry') }}
      </CVButton>
    </div>

    <!-- Station preview -->
    <section
      class="overflow-hidden rounded-lg border border-obsidian-100 bg-gradient-to-br from-obsidian-50 to-white"
    >
      <div
        class="flex flex-wrap items-center justify-between gap-4 border-b border-obsidian-100 px-5 py-4"
      >
        <div class="flex items-center gap-2">
          <MonitorPlayIcon class="size-5 text-hazel-600" />
          <h2 class="text-lg font-semibold">{{ t('previewTitle') }}</h2>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-sm text-obsidian-500">{{ t('previewFor') }}</span>
          <div class="min-w-52">
            <Select v-model="previewStationId" :options="stationOptions" empty-value-to="null" />
          </div>
        </div>
      </div>

      <p
        v-if="previewVideoWallUrl"
        class="flex flex-wrap items-center gap-2 border-b border-obsidian-100 px-5 py-3 text-sm text-obsidian-600"
      >
        {{ t('videoWallUrl') }}:
        <a
          :href="previewVideoWallUrl"
          class="text-primary underline"
          target="_blank"
          rel="noopener"
        >
          {{ previewVideoWallUrl }}
        </a>
        <CopyToClipboardButton :text="previewVideoWallUrl" />
      </p>

      <div class="px-5 py-5">
        <div v-if="playablePool.length" class="grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
          <button
            v-for="item in playablePool"
            :key="item.entry.id"
            type="button"
            class="flex items-center gap-3 rounded-md text-left transition"
            @click="handlePreviewMediaClick(item.entry.media.id)"
          >
            <div class="aspect-video w-20 shrink-0 overflow-hidden rounded-md bg-obsidian-700">
              <img
                v-if="item.entry.media.posterFileUrl"
                :src="item.entry.media.posterFileUrl"
                class="h-full w-full object-cover"
                alt=""
              />
              <div v-else class="flex h-full w-full items-center justify-center text-white">
                <VideoIcon class="size-5" />
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-2">
                <p class="truncate text-sm font-medium">{{ item.entry.media.title }}</p>
                <span class="shrink-0 text-sm font-semibold text-obsidian-900"
                  >{{ formatPercent(item.probability) }}
                </span>
              </div>
              <div class="mt-1 h-2 w-full overflow-hidden rounded-full bg-obsidian-100">
                <div
                  class="h-full rounded-full bg-hazel-500 transition-all"
                  :style="{ width: `${Math.max(2, item.probability * 100)}%` }"
                />
              </div>
              <div class="mt-1 flex items-center gap-2 text-xs text-obsidian-400">
                <span>{{ t('weightShort', { weight: item.entry.weight }) }}</span>
                <span
                  v-if="item.isOverride"
                  class="inline-flex items-center gap-1 rounded-full bg-hazel-100 px-1.5 py-0.5 font-medium text-hazel-700"
                >
                  <MapPinIcon class="size-3" /> {{ t('override') }}
                </span>
              </div>
            </div>
          </button>
        </div>

        <p v-else class="py-6 text-center text-sm text-obsidian-500">{{ t('previewEmpty') }}</p>

        <div
          v-if="inactivePool.length"
          class="mt-5 border-t border-obsidian-100 pt-4 text-sm text-obsidian-500"
        >
          <p class="mb-2 font-medium text-obsidian-600">{{ t('notPlaying') }}</p>
          <ul class="flex flex-wrap gap-2">
            <li
              v-for="item in inactivePool"
              :key="item.entry.id"
              class="inline-flex items-center gap-1.5 rounded-full bg-obsidian-50 px-2.5 py-1"
            >
              <button
                type="button"
                class="max-w-40 truncate text-left hover:underline"
                @click="handlePreviewMediaClick(item.entry.media.id)"
              >
                {{ item.entry.media.title }}
              </button>
              <span class="text-obsidian-400">·</span>
              <span class="text-xs">{{ item.reason }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Videos in rotation -->
    <section class="space-y-4">
      <div class="flex items-baseline justify-between">
        <h2 class="text-lg font-semibold">{{ t('videos') }}</h2>
        <span v-if="videos.length" class="text-sm text-obsidian-400">
          {{ t('videoCount', { count: videos.length }) }}
        </span>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <Spinner class="text-hazel-600" />
      </div>

      <EntityListExceptions
        v-else
        :status="status"
        :error="error as any"
        :items-count="videos.length"
        :no-items-text="t('noVideos')"
      />

      <div v-if="videos.length" class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <RotationVideoCard
          v-for="video in videos"
          :key="video.media.id"
          :video="video"
          @edit="handleEdit"
        />
      </div>
    </section>

    <MediaRotationConfigModal
      v-model="showModal"
      :media="editingVideo?.media ?? null"
      :entries="editingVideo?.entries ?? []"
      :is-loading="isSaving"
      @save="handleSave"
      @remove="handleRemove"
    />
  </div>
</template>

<script lang="ts" setup>
import CVButton from '@/components/buttons/CVButton.vue';
import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import Select from '@/components/Select.vue';
import Spinner from '@/components/icons/Spinner.vue';
import type { RotationVideoConfig } from '@/entities/media-rotation/rotation-media.entity';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import {
  useCreateMediaRotationEntry,
  useDeleteMediaRotationEntry,
  useMediaRotationList,
  useUpdateMediaRotationEntry,
} from '@/queries/use-media-rotation';
import { useStations } from '@/queries/use-stations';
import { Alert } from '@/services/alert.service';
import { MediaEncodingStatus, type RotationMediaResponseDto } from '@carvia/ros-client-types';
import { useAbility } from '@casl/vue';
import { MapPinIcon, MonitorPlayIcon, PlusIcon, VideoIcon } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import RotationVideoCard from './RotationVideoCard.vue';
import MediaRotationConfigModal, {
  type RotationConfigPayload,
} from './MediaRotationConfigModal.vue';

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();
const { can } = useAbility();

const canCreate = computed(() => can('create', 'RotationMedia'));

const GLOBAL_KEY = 'global';

const { data, status, error, isLoading } = useMediaRotationList();
const entries = computed(() => data.value ?? []);

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const { mutateAsync: createEntry } = useCreateMediaRotationEntry();
const { mutateAsync: updateEntry } = useUpdateMediaRotationEntry();
const { mutateAsync: deleteEntry } = useDeleteMediaRotationEntry();

const showModal = ref(false);
const editingVideo = ref<RotationVideoConfig | null>(null);
const isSaving = ref(false);
const previewStationId = ref<string | null>(null);

const stationOptions = computed(() => [
  { label: t('allStations'), value: null },
  ...stations.value.map((station) => ({
    label: getLocalizedField(station.info),
    value: station.id,
  })),
]);

const previewStation = computed(() =>
  previewStationId.value
    ? (stations.value.find((station) => station.id === previewStationId.value) ?? null)
    : null,
);

const previewVideoWallUrl = computed(() => {
  const station = previewStation.value;
  if (!station?.slug || !station.authCode) return null;
  return `${window.location.origin}/video-wall/${station.slug}?code=${station.authCode}`;
});

const videos = computed<RotationVideoConfig[]>(() => {
  const map = new Map<string, RotationVideoConfig>();
  for (const entry of entries.value) {
    let video = map.get(entry.media.id);
    if (!video) {
      video = { media: entry.media, entries: [], globalEntry: null, overrides: [] };
      map.set(entry.media.id, video);
    }
    video.entries.push(entry);
    if (entry.station) {
      video.overrides.push(entry);
    } else {
      video.globalEntry = entry;
    }
  }
  return [...map.values()].sort((a, b) => a.media.title.localeCompare(b.media.title));
});

type PoolItem = {
  entry: RotationMediaResponseDto;
  isOverride: boolean;
  probability: number;
  reason?: string;
};

const effectivePool = computed(() => {
  const map = new Map<string, { entry: RotationMediaResponseDto; isOverride: boolean }>();
  for (const entry of entries.value) {
    if (!entry.station) {
      map.set(entry.media.id, { entry, isOverride: false });
    }
  }
  if (previewStationId.value) {
    for (const entry of entries.value) {
      if (entry.station?.id === previewStationId.value) {
        map.set(entry.media.id, { entry, isOverride: true });
      }
    }
  }
  return [...map.values()];
});

const playablePool = computed<PoolItem[]>(() => {
  const playable = effectivePool.value.filter(
    ({ entry }) => entry.weight > 0 && entry.media.encodingStatus === MediaEncodingStatus.READY,
  );
  const total = playable.reduce((sum, { entry }) => sum + entry.weight, 0);
  return playable
    .map(({ entry, isOverride }) => ({
      entry,
      isOverride,
      probability: total > 0 ? entry.weight / total : 0,
    }))
    .sort((a, b) => b.probability - a.probability);
});

const inactivePool = computed<PoolItem[]>(() =>
  effectivePool.value
    .filter(
      ({ entry }) => entry.weight <= 0 || entry.media.encodingStatus !== MediaEncodingStatus.READY,
    )
    .map(({ entry, isOverride }) => ({
      entry,
      isOverride,
      probability: 0,
      reason: entry.weight <= 0 ? t('paused') : t('notReady'),
    })),
);

const formatPercent = (value: number) =>
  `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: value >= 0.1 ? 0 : 1 })}%`;

function handleAdd() {
  editingVideo.value = null;
  showModal.value = true;
}

function handlePreviewMediaClick(mediaId: string) {
  const video = videos.value.find((entry) => entry.media.id === mediaId);
  if (video) handleEdit(video);
}

function handleEdit(video: RotationVideoConfig) {
  editingVideo.value = video;
  showModal.value = true;
}

async function handleSave(payload: RotationConfigPayload) {
  isSaving.value = true;
  try {
    const existingByKey = new Map<string, RotationMediaResponseDto>();
    for (const entry of editingVideo.value?.entries ?? []) {
      existingByKey.set(entry.station?.id ?? GLOBAL_KEY, entry);
    }

    const desiredByKey = new Map<string, { stationId: string | null; weight: number }>();
    if (payload.global.include) {
      desiredByKey.set(GLOBAL_KEY, { stationId: null, weight: payload.global.weight });
    }
    for (const override of payload.overrides) {
      desiredByKey.set(override.stationId, {
        stationId: override.stationId,
        weight: override.weight,
      });
    }

    for (const [key, desired] of desiredByKey) {
      const existing = existingByKey.get(key);
      if (!existing) {
        await createEntry({
          mediaId: payload.mediaId,
          stationId: desired.stationId,
          weight: desired.weight,
        });
      } else if (existing.weight !== desired.weight || existing.media.id !== payload.mediaId) {
        await updateEntry({
          id: existing.id,
          entry: { weight: desired.weight, mediaId: payload.mediaId },
        });
      }
    }

    for (const [key, existing] of existingByKey) {
      if (!desiredByKey.has(key)) {
        await deleteEntry(existing.id);
      }
    }

    showModal.value = false;
  } catch {
    await Alert.fire({
      titleText: t('saveErrorTitle'),
      text: t('saveErrorText'),
      icon: 'error',
    });
  } finally {
    isSaving.value = false;
  }
}

async function handleRemove() {
  const video = editingVideo.value;
  if (!video) return;

  const { isConfirmed } = await Alert.fire({
    titleText: t('confirmRemoveTitle'),
    text: t('confirmRemoveText'),
    icon: 'warning',
    buttons: [
      { text: t('remove'), style: 'danger', propertyName: 'isConfirmed' },
      { text: t('cancel'), style: 'ghost', propertyName: 'isDismissed' },
    ],
  });
  if (!isConfirmed) return;

  isSaving.value = true;
  try {
    for (const entry of video.entries) {
      await deleteEntry(entry.id);
    }
    showModal.value = false;
  } catch {
    await Alert.fire({
      titleText: t('saveErrorTitle'),
      text: t('saveErrorText'),
      icon: 'error',
    });
  } finally {
    isSaving.value = false;
  }
}
</script>

<i18n lang="json">
{
  "en": {
    "intro": "Decide which videos play on the counter video walls and how often. Weights set the relative play frequency; station overrides take precedence over global entries.",
    "addEntry": "Add to rotation",
    "previewTitle": "Rotation preview",
    "previewFor": "Preview for",
    "allStations": "Global rotation",
    "previewEmpty": "Nothing is currently playing for this selection.",
    "videoWallUrl": "Video wall URL",
    "notPlaying": "Not playing",
    "paused": "Paused",
    "notReady": "Not ready",
    "override": "Station override",
    "weightShort": "Weight {weight}",
    "videos": "Videos in rotation",
    "videoCount": "{count} videos",
    "noVideos": "No videos in rotation yet. Add one to get started.",
    "confirmRemoveTitle": "Remove video from rotation?",
    "confirmRemoveText": "All global and station weightings for this video will be removed.",
    "remove": "Remove",
    "cancel": "Cancel",
    "saveErrorTitle": "Could not save changes",
    "saveErrorText": "Something went wrong while saving the rotation for this video."
  },
  "de": {
    "intro": "Lege fest, welche Videos auf den Counter-Videowänden laufen und wie oft. Gewichtungen bestimmen die relative Abspielhäufigkeit; Stationsüberschreibungen haben Vorrang vor globalen Einträgen.",
    "addEntry": "Zur Rotation hinzufügen",
    "previewTitle": "Rotationsvorschau",
    "previewFor": "Vorschau für",
    "allStations": "Globale Rotation",
    "previewEmpty": "Für diese Auswahl wird derzeit nichts abgespielt.",
    "videoWallUrl": "Video-Wall-URL",
    "notPlaying": "Wird nicht abgespielt",
    "paused": "Pausiert",
    "notReady": "Nicht bereit",
    "override": "Stationsüberschreibung",
    "weightShort": "Gewichtung {weight}",
    "videos": "Videos in der Rotation",
    "videoCount": "{count} Videos",
    "noVideos": "Noch keine Videos in der Rotation. Füge eines hinzu, um zu starten.",
    "confirmRemoveTitle": "Video aus der Rotation entfernen?",
    "confirmRemoveText": "Alle globalen und stationsspezifischen Gewichtungen für dieses Video werden entfernt.",
    "remove": "Entfernen",
    "cancel": "Abbrechen",
    "saveErrorTitle": "Änderungen konnten nicht gespeichert werden",
    "saveErrorText": "Beim Speichern der Rotation für dieses Video ist ein Fehler aufgetreten."
  },
  "es": {
    "intro": "Decide qué vídeos se reproducen en las pantallas del mostrador y con qué frecuencia. Los pesos definen la frecuencia relativa; las anulaciones por estación tienen prioridad sobre las entradas globales.",
    "addEntry": "Añadir a la rotación",
    "previewTitle": "Vista previa de la rotación",
    "previewFor": "Vista previa para",
    "allStations": "Rotación global",
    "previewEmpty": "No se está reproduciendo nada para esta selección.",
    "videoWallUrl": "URL del video wall",
    "notPlaying": "No se reproduce",
    "paused": "En pausa",
    "notReady": "No está listo",
    "override": "Anulación de estación",
    "weightShort": "Peso {weight}",
    "videos": "Vídeos en rotación",
    "videoCount": "{count} vídeos",
    "noVideos": "Aún no hay vídeos en la rotación. Añade uno para empezar.",
    "confirmRemoveTitle": "¿Quitar el vídeo de la rotación?",
    "confirmRemoveText": "Se eliminarán todos los pesos globales y por estación de este vídeo.",
    "remove": "Quitar",
    "cancel": "Cancelar",
    "saveErrorTitle": "No se pudieron guardar los cambios",
    "saveErrorText": "Algo salió mal al guardar la rotación de este vídeo."
  }
}
</i18n>
